import React from "react";
import InputLabel from "./InputLabel";
import ErrorMessage from "../ErrorMessages/ErrorMessage";
import { getMaxLength } from "../commonfunctions/getMaxLength";
import useTranslation from "../CustomHooks/useTranslation";
import { inputFieldContent } from "./InputFieldContent";
import InputShimmer from "./InputShimmer";

interface InputItem {
  labelName: string;
  id: string;
}

interface CurrencyInputProps {
  inputItem: InputItem;
  errorMessage?: string;
  required?: boolean;
  onChange?: (event: React.ChangeEvent<HTMLInputElement>) => void;
  fieldError?: string | boolean;
  tabIndex: number;
  maxLength?: number;
  testId?: string;
  disabled?: boolean;
  value?: string;
  className?: string;
  maindivstyle?: string;
  editLoading?: boolean;
  placeholder?: string;
  secondaryText?: string;
  customWidth?: string;
  currency?: string; // New prop for currency code
}

const CurrencyInputField: React.FC<CurrencyInputProps> = ({
  inputItem,
  errorMessage,
  onChange,
  required,
  fieldError,
  maxLength,
  testId,
  disabled,
  value,
  className,
  maindivstyle,
  editLoading,
  placeholder,
  secondaryText,
  customWidth,
  currency = "AFA", // Default currency
}) => {
  const { labelName, id } = inputItem;
  const { translate, notEnglish } = useTranslation(inputFieldContent);

  const isFirefox = navigator.userAgent.toLowerCase().indexOf('firefox') > -1;
  
  // Handle change event with numeric validation
  const handleChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const maxLength = getMaxLength(id);
    let { value } = event.target;

    // Only allow numbers and a single decimal point
    if (!/^\d*\.?\d*$/.test(value) && value !== '') {
      return; // Reject non-numeric input
    }

    // Handle decimal points
    const parts = value.split('.');
    if (parts[1] && parts[1].length > 2) {
      value = `${parts[0]}.${parts[1].slice(0, 2)}`;
    }

    // Restrict whole number part to 10 digits and decimal part to 2 digits
    if (parts[0] && parts[0].length > 10) {
      value = `${parts[0].slice(0, 10)}${parts[1] ? `.${parts[1]}` : ''}`;
    }

    // Update the value of the input field
    event.target.value = value;
    
    if (onChange) {
      onChange(event); // Trigger the parent onChange handler
    }
  };

  return (
    <div className={`${maindivstyle}`}>
      <InputLabel labelName={labelName} id={id} required={required} />
      {editLoading ? (
        <InputShimmer />
      ) : (
        <>
          <div 
            className={`flex items-center bg-white ${customWidth ? customWidth : 'w-80'} h-14 border rounded-lg ${
              errorMessage || fieldError ? "border-negative" : "border-P_light_active"
            } ${disabled ? "bg-disable_fill border-disable_stroke" : ""} focus-within:border-2`}
          >
            {!notEnglish && value && (
              <div className={`flex items-center pl-3  ${isFirefox ? '' : 'pt-[2.5px]'} text-subTitle ${disabled ? "bg-disable_fill text-disable_text h-full rounded-l-lg" : "bg-white"}`}>
                {currency}
              </div>
            )}
            <input
              id={id}
              data-testid={testId || id}
              className={`${className} ${customWidth} flex-1 h-full py-5 pr-[15px] pl-2.5 focus:pl-[9px] disabled:cursor-not-allowed 
                disabled:bg-disable_fill text-subTitle outline-none rounded-r-lg ${
                disabled ? "bg-disable_fill" : "bg-white rounded-lg"
              }`}
              placeholder={placeholder ? translate(placeholder) : translate(`Enter amount`)}
              onChange={handleChange}
              autoComplete="off"
              maxLength={maxLength}
              disabled={disabled}
              value={value}
            />
            {notEnglish && value && (
              <div className={`flex items-center pl-3 ${isFirefox ? '' : 'pt-[2.5px]'} text-subTitle ${disabled ? "bg-disable_fill text-disable_text" : "bg-white"}`}>
                {currency}
              </div>
            )}
          </div>
          {secondaryText && <p className="mt-1 text-body2 text-secondary_text">{secondaryText}</p>}
        </>
      )}
      {(errorMessage && !disabled) && <ErrorMessage errorMessage={errorMessage} />}
    </div>
  );
};

export default CurrencyInputField;
